/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 26.06.25
 *
 */
import {Type} from "@qwoach/class-transformer-wrapper";
import LineItem from "@shared/Invoice/LineItem";
import CurrencyItem, {CURRENCY_FORMATTING_OPTIONS_SHORT, currencyFormatted} from "@shared/Profile/CurrencyItem";
import {ensureArray, timeNow} from "@/utils/helpers";
import FilterableItem from "@shared/Filterable/FilterableItem";
import {getStatusNameFromStatus, InvoiceStatus} from "@shared/Invoice/InvoiceStatus";
import dayjs from "dayjs";
import LineItemGroup from "@shared/Invoice/LineItemGroup";
import BankingInfo from "@shared/Billing/BankingInfo";
import {ServiceType} from "@shared/Quote/ServiceType";
import ContactDetailsItem from "@shared/Invoice/ContactDetailsItem";

const FUNDING_TRACE_PREFIX = "[FUNDING_TRACE]";

export default class InvoiceItem extends FilterableItem {

	status: string = InvoiceStatus.DRAFT

	ownerId: string = null
	clientId: string = null
	fundingSourceId: string = null

	title: string = null
	refNumber: string = null

	asfPurchaseOrderNumber: string = null
	asfNumber: string = null
	localAuthorityId: string = null

	@Type(() => ContactDetailsItem)
	senderContactDetails: ContactDetailsItem = new ContactDetailsItem()
	senderId: string = null

	@Type(() => ContactDetailsItem)
	recipientContactDetails: ContactDetailsItem = new ContactDetailsItem()
	recipientId: string = null

	@Type(() => BankingInfo)
	bankingInfo: BankingInfo = null

	@Type(() => LineItem)
	lineItems: LineItem[] = []

	groupOrder: string[] = []

	currencyCode: string = CurrencyItem.USD.currencyCode

	invoiceDateMs: number = null
	sentDateMs: number = null
	dueDateMs: number = null
	paidDateMs: number = null

	footerNote: String = null

	//the below props are only used for invoice rendering. Not saved to DB
	_titleFull: string = null
	_totalPrice: string = null
	_bankingInfoFormatted: string = null
	_lineItemsTimeRangeFormatted: string = null
	_lineItemsFinal: LineItem[] = []
	_residueFunding: number = 0
	_allInvoiceItemsRef = null



	constructor(options = {}) {
		super(options);

		this.status = options.status || InvoiceStatus.DRAFT;

		this.clientId = options.clientId || null;
		this.senderId = options.senderId || null;
		this.recipientId = options.recipientId || null;
		this.sentDateMs = options.sentDateMs || null;
		this.dueDateMs = options.dueDateMs || null;
		this.paidDateMs = options.paidDateMs || null;
		this.ownerId = options.ownerId || null;
		this.title = options.title || null;
		this.groupOrder = options.groupOrder || [];
		this.refNumber = options.refNumber || null;
		this.asfPurchaseOrderNumber = options.asfPurchaseOrderNumber || null;
		this.asfNumber = options.asfNumber || null;
		this.localAuthorityId = options.localAuthorityId || null;
		this.footerNote = options.footerNote || "With Thanks";
		this.bankingInfo = options.bankingInfo || new BankingInfo();
		this.fundingSourceId = options.fundingSourceId || null;

		let invoiceDateMs = options.invoiceDateMs || timeNow()
		let currencyCode = options.currencyCode || CurrencyItem.USD.currencyCode;

		this.setInvoiceDate(invoiceDateMs);
		this.setCurrencyCode(currencyCode);



		this.addLineItems(options.lineItems || []);
		this.resetInvoiceDate()
		this.setupRefNumber();
	}

	setCurrencyCode(currencyCode) {
		this.currencyCode = currencyCode;
		this.lineItems.forEach(lineItem => lineItem.setCurrencyCode(currencyCode));
	}

	isStatusPreview() {
		return this.status === InvoiceStatus.PREVIEW;
	}

	setStatusPreview(){
		this.status = InvoiceStatus.PREVIEW;
	}

	setStatusSent() {
		this.status = InvoiceStatus.SENT;
		this.sentDateMs = timeNow();
	}

	setStatusPaid() {
		this.status = InvoiceStatus.PAID;
		this.paidDateMs = timeNow();
	}

	statusTimeMs(){
		switch (this.status) {
			case InvoiceStatus.SENT:
				return this.sentDateMs;
			case InvoiceStatus.PAID:
				return this.paidDateMs;
			default:
				return null;
		}
	}

	statusTimeFormatted() {
		let timeMs = this.statusTimeMs();
		if (!timeMs) {
			return "";
		}
		return dayjs(timeMs).format("DD MMM, YYYY");
	}

	setSenderFromUserProfile(userProfile) {
		if(!userProfile) {
			console.warn("InvoiceItem.setSenderFromUserProfile: userProfile is not provided. Cannot set sender for invoiceItem = ", this);
			return this
		}
		this.ownerId = userProfile.id;
		this.setCurrencyCode(userProfile.getCurrencyCode());
		this.senderContactDetails.setFromUserProfile(userProfile);
		this.bankingInfo = userProfile.bankingInfo;
		return this
	}

	setRecipientFromFundingSource(fundingSource, options = {}) {
		if(!fundingSource){
			console.warn("InvoiceItem.setRecipientFromFundingSource: fundingSource is not provided. Cannot set recipient for invoiceItem = ", this);
			return
		}
		fundingSource.fillInvoice(this, options)
	}

	computeValues(commissionDetails = null) {
		this.sortLineItems();
		this.lineItems.forEach(lineItem => {
			this.assignLineItemContext(lineItem)
			lineItem.computeValues(commissionDetails)
		});
		this._totalPrice = this.getTotalPriceFormatted()
		this._bankingInfoFormatted = this.bankingInfo ? this.bankingInfo.formatted({accountName: this.senderName}) : "";
		this._lineItemsTimeRangeFormatted = this.lineItemsTimeRangeFormatted()
		this._lineItemsFinal = this.lineItems.filter((lineItem) => lineItem.getTotalPriceCents() !== 0)
	}

	getFundingSource(commissionDetails) {
		return commissionDetails.getFundingSourceById(this.recipientId)
	}

	setInvoiceItemsScope(invoiceItems = []) {
		this._allInvoiceItemsRef = ensureArray(invoiceItems)
		return this
	}

	assignLineItemContext(lineItem) {
		if (!lineItem) {
			return
		}
		lineItem._invoiceItemRef = this
		lineItem._allInvoiceItemsRef = this._allInvoiceItemsRef || []
		for (let subLineItem of lineItem.subLineItems || []) {
			this.assignLineItemContext(subLineItem)
		}
	}
	getFundingSourceName(commissionDetails) {
		return this.getFundingSource(commissionDetails)?.getName()
	}

	// getResidueAmount(commissionDetails) {
	// 	let fundingSource = this.getFundingSource(commissionDetails)
	//
	// 	if(!fundingSource){
	// 		return null
	// 	}
	//
	//
	// 	let invoicedTotalCents = this.getTotalLoggedCents(commissionDetails)
	//
	// 	let invoicedTotal = invoicedTotalCents / 100
	//
	// 	let funding = this.getAllocatedFunding(commissionDetails)
	// 	return funding - invoicedTotal
	// }

	getResidueAmount(commissionDetails) {

		let invoicedTotalCents = this.getTotalLoggedCents(commissionDetails)

		let funding = this.getAllocatedFunding(commissionDetails)
		try {
			console.log(`${FUNDING_TRACE_PREFIX} InvoiceItem.getResidueAmount ${JSON.stringify({
				invoiceId: this.id,
				invoiceDateMs: this.invoiceDateMs,
				invoiceMonth: this.invoiceMonthFormatted?.() || null,
				status: this.status,
				recipientId: this.recipientId,
				fundingSourceName: this.getFundingSourceName?.(commissionDetails) || null,
				invoiceTotalPriceCents: this.getTotalPriceCents(),
				allocatedFundingCents: funding,
				invoicedTotalCentsByEstimateRule: invoicedTotalCents,
				estimatedResidueFundingCents: funding - invoicedTotalCents,
				snapshot: commissionDetails?.getFundingTraceSnapshot?.({
					invoiceDateMs: this.invoiceDateMs,
				}) || null,
			})}`)
		} catch (error) {
			console.log(`${FUNDING_TRACE_PREFIX} InvoiceItem.getResidueAmount`, this, error)
		}
		return (funding - invoicedTotalCents) / 100
	}

	// getAllocatedFunding(commissionDetails) {
	// 	let fundingSource = this.getFundingSource(commissionDetails)
	// 	return fundingSource?.getFinancialYearFundingTotal(this.invoiceDateMs) || 0
	// }

	getAllocatedFunding(commissionDetails) {
		return commissionDetails.quoteItem.getTotalAmountCents()
	}

	getTotalLoggedCents(commissionDetails) {
		let fundingSource = this.getFundingSource(commissionDetails)

		return commissionDetails.getLogValueTotal((serviceItem, {isDraft}) => {
			// console.log("InvoiceItem.getResidueAmountCents: serviceItem = ", serviceItem, ", fundingSource = ", fundingSource, ", commissionDetails = ", commissionDetails, ", this = ", this);
			if(serviceItem.serviceType === ServiceType.INVOICE_TOTAL_AMOUNT
				&& (!isDraft || serviceItem.serviceDateMs <= this.invoiceDateMs)
				// && fundingSource && (!fundingSource.hasFinancialYear() || fundingSource.sameFinancialYear([serviceItem.serviceDateMs, this.invoiceDateMs]))
				// && serviceItem.filterParams.recipientId === this.recipientId;
			){
				return serviceItem.usedQuantity
			}
		});
	}

	sortLineItems(){
		if(this.lineItems.length === 0) {
			return;
		}
		const groupNameOther = "OTHER";
		const groupOrder = [...this.groupOrder];
		if (!groupOrder.includes(groupNameOther)) groupOrder.push(groupNameOther);

		// Build group index map
		const groupIndexMap = Object.fromEntries(
			groupOrder.map((name, idx) => [name.toUpperCase(), idx])
		);

		// Group line items
		const groups = Array(groupOrder.length).fill().map(() => []);
		for (const lineItem of this.lineItems) {
			const groupName = (lineItem.groupName || this.getLineItemGroupNameByType(lineItem.serviceType) || groupNameOther).toUpperCase();
			const idx = groupIndexMap[groupName] ?? groupIndexMap[groupNameOther];
			groups[idx].push(lineItem);
		}

		//sort within every group by date and tehn by id if date is the same

		for (let group of groups) {
			group.sort((a, b) => {
				if (a.serviceDateMs === b.serviceDateMs) {
					return a.id.localeCompare(b.id);
				}
				return (a.serviceDateMs || 0) - (b.serviceDateMs || 0);
			});
		}

		// Flatten groups
		this.lineItems = groups.flat();
		console.log("InvoiceItem.sortLineItems: groups = ", groups, ", groupOrder = ", groupOrder, ", this.lineItems = ", this.lineItems);
	}

	resetInvoiceDate() {
		this.setInvoiceDate(timeNow())
	}

	setInvoiceDate(dateMs) {
		this.invoiceDateMs = dateMs;
		this.lineItems.forEach(lineItem => lineItem.setDateIfNeeded(dateMs));
	}

	setupRefNumber(){
		if (!this.refNumber) {
			let date = dayjs(this.invoiceDateMs);
			this.refNumber = `INV-${date.format("YYYYMMDD")}-${date.format("HH")}`;
		}
	}

	getTotalPrice() {
		return this.getTotalPriceCents() / 100;
	}

	getTotalPriceCents() {
		return this.lineItems.reduce((total, lineItem) => {
			return total + lineItem.getTotalPriceCents();
		}, 0);
	}

	getTotalPriceFormatted(currencyCode = this.currencyCode) {
		return currencyFormatted(currencyCode, this.getTotalPrice(), CURRENCY_FORMATTING_OPTIONS_SHORT);
	}

	lineItemsTimeRangeFormatted(){
		//Find smallest date in line items and biggest date in line items.
		//Then format as "MMMM, YYYY" if date months and years are the same, or "MMMM - MMMM, YYYY" if they are different

		if (this.lineItems.length === 0) {
			return "";
		}

		let dates = this.lineItems.map(lineItem => lineItem.serviceDateMs).filter(dateMs => dateMs);
		if (dates.length === 0) {
			return "";
		}
		let minDate = Math.min(...dates);
		let maxDate = Math.max(...dates);


		let res = "";

		let minDateObj = dayjs(minDate);
		let maxDateObj = dayjs(maxDate);

		if (minDateObj.month() === maxDateObj.month() && minDateObj.year() === maxDateObj.year()) {
			res = minDateObj.format("MMM YYYY");
		} else if (minDateObj.year() === maxDateObj.year()) {
			res = `${minDateObj.format("MMM")} - ${maxDateObj.format("MMM YYYY")}`;
		} else {
			res = `${minDateObj.format("MMM YYYY")} - ${maxDateObj.format("MMM YYYY")}`;
		}


		return res;
	}


	getLineItemById(lineItemId) {
		return this.lineItems.find(lineItem => lineItem.id === lineItemId);
	}

	addLineItems(lineItems: LineItem[]) {
		lineItems = ensureArray(lineItems || [])
		for (let lineItem of lineItems) {
			lineItem.setCurrencyCode(this.currencyCode);
			lineItem.setDateIfNeeded(this.invoiceDateMs);
			this.lineItems.pushUnique(lineItem);
		}
	}

	removeLineItems(lineItems: LineItem[]) {
		lineItems = ensureArray(lineItems || [])
		for (let lineItem of lineItems) {
			this.lineItems.removeItem(lineItem);
		}
	}

	lineItemsGrouped(options = {}) {
		let groupsOrder = options.groupsOrder || this.groupOrder || [];
		let lineItemsFilter = options.lineItemsFilter || (() => true);
		let allGroups = this.lineItems
			.filter(lineItemsFilter)
			.reduce((groups, lineItem) => {
			let groupName = lineItem.groupName || this.getLineItemGroupNameByType(lineItem.serviceType)
			let groupNameUppercase = groupName.toUpperCase();
			if (!groups[groupNameUppercase]) {
				groups[groupNameUppercase] = {
					groupName,
					lineItems: [],
				};
			}
			groups[groupNameUppercase].lineItems.push(lineItem);
			return groups;
		}, {});

		let orderedGroups = [];
		if (groupsOrder.length > 0) {
			for (let groupName of groupsOrder) {
				let groupNameUpperCase = groupName.toUpperCase();
				if (allGroups[groupNameUpperCase]) {
					let group = allGroups[groupNameUpperCase]
					orderedGroups.push(new LineItemGroup(group))
					delete allGroups[groupNameUpperCase]
				}
			}
		}
		Object.keys(allGroups).forEach(groupKey => {
			let group = allGroups[groupKey];
			orderedGroups.push(new LineItemGroup(group))
		})

		return orderedGroups;

	}

	getLineItemsByType(types = []) {
		types = ensureArray(types);

		return this.lineItems.filter(lineItem => types.includes(lineItem.serviceType));
	}

	getLineItemIndex(lineItem) {
		if(!lineItem){
			return -1
		}
		return this.lineItems.findIndex(item => item.id === lineItem.id);
	}

	hasLineItemByType(serviceType) {
		return this.lineItems.some(lineItem => lineItem.serviceType === serviceType);
	}


	invoiceMonthFormatted() {
		if (!this.invoiceDateMs) {
			return "";
		}
		return dayjs(this.invoiceDateMs).format("MMM YYYY");
	}

	statusFormatted() {
		let statusTimeFormatted = this.statusTimeFormatted();
		if(statusTimeFormatted){
			statusTimeFormatted = ` on ${statusTimeFormatted}`
		}
		let statusName = getStatusNameFromStatus(this.status);
		return `${statusName}${statusTimeFormatted}`;
	}

	isDraft() {
		return this.status === InvoiceStatus.DRAFT;
	}

	isSent() {
		return this.status === InvoiceStatus.SENT;
	}

	isPaid() {
		return this.status === InvoiceStatus.PAID;
	}

	isEditable() {
		let editableStatuses = [InvoiceStatus.NOT_SET, InvoiceStatus.DRAFT, null]
		return editableStatuses.includes(this.status);
	}


	getLineItemGroupNameByType(serviceType) {
		switch (serviceType) {
			case ServiceType.SESSION:
				return "Session";
			case ServiceType.TLSW_BOOK_WRITING:
			case ServiceType.AAM_BOOK_WRITING:
				return "book Writing";
			case ServiceType.SESSION_PREPARATION_TIME:
				return "Preparation/Write up";
			case ServiceType.ROUND_TRIP_MILEAGE:
				return "Round Trip Travel";
			default:
				return "Other";
		}
	}

}
